(function setupBimSchvUi(root, factory) {
  'use strict';

  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }

  root.OfenerlebnisBimSchvUi = api;
  api.initialize(root);
})(typeof globalThis !== 'undefined' ? globalThis : this, function createBimSchvUi() {
  'use strict';

  const STATUS_LABELS = Object.freeze({
    PASS: 'Geprüfte Anforderungen erfüllt',
    REVIEW: 'Nachweis prüfen',
    ACTION_REQUIRED: 'Handlungsbedarf prüfen',
    EXCEPTION: 'Sonderfall erkannt',
    UNKNOWN: 'Keine eindeutige Einordnung möglich',
  });

  const STATUS_SUMMARIES = Object.freeze({
    PASS: 'Die geprüften Angaben erfüllen die hinterlegten Anforderungen.',
    REVIEW: 'Für die Einordnung muss ein Nachweis oder eine Angabe genauer geprüft werden.',
    ACTION_REQUIRED: 'Mindestens eine geprüfte Voraussetzung erfordert weiteres Handeln.',
    EXCEPTION: 'Für Ihre Angaben wurde ein besonderer Prüfpfad erkannt.',
    UNKNOWN: 'Die Angaben reichen für eine eindeutige Einordnung nicht aus.',
  });

  const APPLIANCE_LABELS = Object.freeze({
    room_heater_flat: 'Kaminofen / Raumheizer mit Flachfeuerung',
    room_heater_fill: 'Kaminofen / Raumheizer mit Füllfeuerung',
    storage_single: 'Speichereinzelfeuerstätte',
    fireplace_insert_closed: 'Kamineinsatz mit geschlossener Betriebsweise',
    tiled_stove_insert_flat: 'Kachelofeneinsatz mit Flachfeuerung',
    tiled_stove_insert_fill: 'Kachelofeneinsatz mit Füllfeuerung',
    stove: 'Herd',
    heating_stove: 'Heizungsherd',
    pellet_stove_no_water: 'Pelletofen ohne Wassertasche',
    pellet_stove_water: 'Pelletofen mit Wassertasche',
    other_heating: 'Andere Feuerstätte zum Beheizen',
    other_cooking: 'Andere Feuerstätte zum Kochen oder Backen',
    open_fireplace: 'Offener Kamin',
    ground_stove: 'Grundofen',
    unknown: 'Feuerstättenart unsicher',
  });

  const PERIOD_LABELS = Object.freeze({
    pre1950: 'Vor 1950',
    to1974: '1950 bis 1974',
    to1984: '1975 bis 1984',
    to1994: '1985 bis 1994',
    to20100321: '1995 bis 21.03.2010',
    to2014: '22.03.2010 bis 2014',
    from2015: 'Ab 2015',
    unknown: 'Zeitraum nicht bekannt',
  });

  const SUBTYPE_FIELD_NAMES = Object.freeze({
    room_heater: 'applianceTypeRoom',
    pellet: 'applianceTypePellet',
    fireplace_insert: 'applianceTypeFireplace',
    tiled_insert: 'applianceTypeTiled',
    cooking: 'applianceTypeCooking',
    other: 'applianceTypeOther',
  });

  const OLD_PERIODS = Object.freeze(['pre1950', 'to1974', 'to1984', 'to1994', 'to20100321', 'unknown']);
  const INSERT_TYPES = Object.freeze(['fireplace_insert_closed', 'tiled_stove_insert_flat', 'tiled_stove_insert_fill']);
  const COOKING_EXCEPTION_TYPES = Object.freeze(['stove', 'other_cooking']);
  const STAGE_UNSUPPORTED_TYPES = Object.freeze(['ground_stove', 'open_fireplace', 'unknown']);
  const REPRESENTATIVE_TYPES = Object.freeze({
    room_heater: 'room_heater_flat',
    pellet: 'pellet_stove_no_water',
    fireplace_insert: 'fireplace_insert_closed',
    tiled_insert: 'tiled_stove_insert_flat',
    cooking: 'stove',
    other: 'other_heating',
  });
  let pdfModulePromise = null;

  function formatLocalDate(value) {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return 'nicht verfügbar';
    return new Intl.DateTimeFormat('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  function createCheckId(value, windowObject) {
    const date = value instanceof Date ? value : new Date(value || Date.now());
    if (Number.isNaN(date.getTime())) throw new Error('Für die Prüf-ID ist ein gültiges Erstellungsdatum erforderlich.');
    const datePart = [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0'),
    ].join('');
    const bytes = new Uint8Array(4);
    if (windowObject.crypto && typeof windowObject.crypto.getRandomValues === 'function') {
      windowObject.crypto.getRandomValues(bytes);
    } else {
      for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
    }
    const number = bytes.reduce((valueSoFar, byte) => (valueSoFar * 256 + byte) % 2176782336, 0);
    return `OE-BIM-${datePart}-${number.toString(36).toUpperCase().padStart(6, '0')}`;
  }

  function createExportSeed(options, windowObject) {
    const createdAt = new Date();
    return Object.freeze({
      result: options.result,
      input: Object.freeze({ ...options.input }),
      inputItems: Object.freeze([...options.inputItems]),
      checkId: createCheckId(createdAt, windowObject),
      createdAt,
    });
  }

  function loadPdfModule(windowObject, scriptUrl) {
    const currentModule = windowObject.OfenerlebnisBimSchvPdf;
    if (currentModule && typeof currentModule.downloadPdf === 'function') return Promise.resolve(currentModule);
    if (pdfModulePromise) return pdfModulePromise;
    if (!scriptUrl) return Promise.reject(new Error('Die Adresse des PDF-Moduls fehlt.'));

    pdfModulePromise = new Promise((resolve, reject) => {
      const finish = () => {
        const pdf = windowObject.OfenerlebnisBimSchvPdf;
        if (pdf && typeof pdf.downloadPdf === 'function' && typeof pdf.createExportState === 'function') {
          resolve(pdf);
          return;
        }
        script.remove();
        pdfModulePromise = null;
        reject(new Error('Das PDF-Modul wurde geladen, ist aber nicht verfügbar.'));
      };
      const fail = () => {
        pdfModulePromise = null;
        reject(new Error('Das PDF-Modul konnte nicht geladen werden.'));
      };
      const script = windowObject.document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.dataset.bimschvPdfModule = '';
      script.addEventListener('load', finish, { once: true });
      script.addEventListener('error', () => {
        script.remove();
        fail();
      }, { once: true });
      windowObject.document.head.append(script);
    });
    return pdfModulePromise;
  }

  function parseDecimal(rawValue, maximum) {
    const value = String(rawValue == null ? '' : rawValue).trim();
    if (value === '') return { valid: false, empty: true, value: null };
    if (!/^(?:\d+(?:[.,]\d+)?|[.,]\d+)$/.test(value)) {
      return { valid: false, empty: false, value: null };
    }

    const number = Number(value.replace(',', '.'));
    if (!Number.isFinite(number) || number < 0 || (typeof maximum === 'number' && number > maximum)) {
      return { valid: false, empty: false, value: null };
    }

    return { valid: true, empty: false, value: number };
  }

  function formatGermanMeasurementText(value) {
    return String(value == null ? '' : value)
      .replace(/(-?\d+(?:[.,]\d+)?)(?=\s*g\/m³)/g, (match) => {
        const normalized = match.replace(',', '.');
        return normalized.includes('.') ? normalized.replace('.', ',') : `${normalized},0`;
      })
      .replace(/(-?\d+)\.(\d+)(?=\s*%)/g, '$1,$2');
  }

  function buildStepSequence(state) {
    const steps = ['family'];
    if (!state.applianceFamily) return steps;

    if (state.applianceFamily === 'unknown') return steps;

    if (Object.prototype.hasOwnProperty.call(SUBTYPE_FIELD_NAMES, state.applianceFamily)) {
      steps.push(`subtype-${state.applianceFamily}`);
      if (!state.applianceType) return steps;
    }

    steps.push('official');
    if (state.officialExceptionChoice !== 'false') return steps;

    steps.push('period');
    if (!state.period) return steps;

    if (state.period === 'pre1950') {
      steps.push('pre1950-proof');
      if (state.manufacturedBefore1950Choice !== 'false') return steps;
    }

    if (state.applianceType === 'unknown') return steps;

    if (OLD_PERIODS.includes(state.period)) {
      if (state.applianceType === 'open_fireplace') return steps;

      steps.push('sole-heat');
      if (state.soleHeatSourceChoice !== 'false') return steps;

      if (COOKING_EXCEPTION_TYPES.includes(state.applianceType)) {
        steps.push('cooking-exception');
        if (state.noncommercialStoveChoice !== 'false') return steps;
      }

      if (state.applianceType === 'ground_stove') return steps;

      if (INSERT_TYPES.includes(state.applianceType)) {
        steps.push('masonry');
        if (state.masonryInsertChoice === 'true') {
          steps.push('masonry-proof');
          return steps;
        }
        if (state.masonryInsertChoice !== 'false') return steps;
      }

      steps.push('old-proof');
      if (['manufacturer_test', 'chimney_sweep_measurement'].includes(state.oldProofType)) {
        steps.push('measurements');
      }
      return steps;
    }

    if (STAGE_UNSUPPORTED_TYPES.includes(state.applianceType)) return steps;

    steps.push('type-proof');
    if (state.typeProofStatus === 'complete') steps.push('measurements');
    return steps;
  }

  function buildProgressSequence(state) {
    const projected = { ...state };
    if (!projected.applianceFamily) {
      projected.applianceFamily = 'room_heater';
      projected.applianceType = 'room_heater_flat';
    } else if (projected.applianceFamily !== 'unknown' && !projected.applianceType) {
      projected.applianceType = REPRESENTATIVE_TYPES[projected.applianceFamily] || projected.applianceType;
    }

    if (!projected.officialExceptionChoice) projected.officialExceptionChoice = 'false';
    if (!projected.period) projected.period = 'from2015';
    if (!projected.manufacturedBefore1950Choice) projected.manufacturedBefore1950Choice = 'false';
    if (!projected.soleHeatSourceChoice) projected.soleHeatSourceChoice = 'false';
    if (!projected.noncommercialStoveChoice) projected.noncommercialStoveChoice = 'false';
    if (!projected.masonryInsertChoice) projected.masonryInsertChoice = 'false';
    if (!projected.oldProofType) projected.oldProofType = 'manufacturer_test';
    if (!projected.typeProofStatus) projected.typeProofStatus = 'complete';
    return buildStepSequence(projected);
  }

  function initialize(windowObject) {
    if (!windowObject.customElements || windowObject.customElements.get('bimschv-check')) return;

    class BimSchvCheck extends windowObject.HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;

        this.form = this.querySelector('[data-bimschv-form]');
        this.resultRegion = this.querySelector('[data-bimschv-result]');
        this.progress = this.querySelector('[data-wizard-progress]');
        this.progressText = this.querySelector('[data-progress-text]');
        this.progressDetail = this.querySelector('[data-progress-detail]');
        this.progressBar = this.querySelector('[data-progress-bar]');
        this.progressValue = this.querySelector('[data-progress-value]');
        this.backButton = this.querySelector('[data-action="back"]');
        this.nextButton = this.querySelector('[data-action="next"]');
        this.stepElements = new Map(
          Array.from(this.querySelectorAll('[data-step]')).map((element) => [element.dataset.step, element])
        );

        if (!this.form || !this.resultRegion || !this.nextButton) return;

        this.dataset.initialized = 'true';
        this.currentIndex = 0;
        this.lastInput = null;
        this.lastResult = null;
        this.lastExportState = null;
        this.lastCheckId = null;
        this.lastInputSummary = null;
        this.pdfScriptUrl = this.dataset.pdfScriptUrl || '';
        this.handleNext = this.handleNext.bind(this);
        this.handleBack = this.handleBack.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleRestart = this.handleRestart.bind(this);

        this.nextButton.addEventListener('click', this.handleNext);
        this.backButton?.addEventListener('click', this.handleBack);
        this.form.addEventListener('change', this.handleChange);
        this.form.addEventListener('input', this.handleChange);
        this.form.addEventListener('submit', (event) => event.preventDefault());
        this.resultRegion.addEventListener('click', (event) => {
          const action = event.target.closest('[data-action]');
          if (!action) return;
          if (action.dataset.action === 'restart') this.handleRestart();
          if (action.dataset.action === 'pdf') this.handlePdfDownload(action);
          if (action.dataset.action === 'print') this.handlePrint(action);
        });

        this.setupStepAccessibility();
        this.updateUnknownControls();
        this.renderStep(false);
      }

      getRadioValue(name) {
        return this.form.querySelector(`input[name="${name}"]:checked`)?.value || '';
      }

      getState() {
        const applianceFamily = this.getRadioValue('applianceFamily');
        const familyInput = this.form.querySelector('input[name="applianceFamily"]:checked');
        const subtypeName = SUBTYPE_FIELD_NAMES[applianceFamily];
        const applianceType = familyInput?.dataset.applianceType || (subtypeName ? this.getRadioValue(subtypeName) : '');

        return {
          scopeConfirmed: applianceFamily === 'unknown' ? 'false' : applianceFamily ? 'true' : '',
          officialExceptionChoice: this.getRadioValue('officialExceptionChoice'),
          applianceFamily,
          applianceType,
          period: this.getRadioValue('period'),
          manufacturedBefore1950Choice: this.getRadioValue('manufacturedBefore1950Choice'),
          soleHeatSourceChoice: this.getRadioValue('soleHeatSourceChoice'),
          noncommercialStoveChoice: this.getRadioValue('noncommercialStoveChoice'),
          masonryInsertChoice: this.getRadioValue('masonryInsertChoice'),
          masonryDustReductionProof: this.getRadioValue('masonryDustReductionProof'),
          oldProofType: this.getRadioValue('oldProofType'),
          typeProofStatus: this.getRadioValue('typeProofStatus'),
        };
      }

      handleChange(event) {
        const step = event.target.closest('[data-step]');
        if (step) this.clearStepError(step);

        if (event.target.matches('[name$="Unknown"]')) this.updateUnknownControl(event.target);
        if (event.target.matches('[name="co"], [name="dust"], [name="efficiency"]')) {
          this.clearFieldError(event.target.name);
        }
        if (event.type === 'change' && event.target.matches('input[type="radio"]')) this.renderStep(false);
      }

      handleNext() {
        const stateBefore = this.getState();
        const sequenceBefore = buildStepSequence(stateBefore);
        const currentId = sequenceBefore[this.currentIndex] || sequenceBefore[sequenceBefore.length - 1];
        const currentStep = this.stepElements.get(currentId);
        if (!currentStep || !this.validateStep(currentStep, currentId)) return;

        const sequenceAfter = buildStepSequence(this.getState());
        const currentPosition = sequenceAfter.indexOf(currentId);
        if (currentPosition >= 0 && currentPosition < sequenceAfter.length - 1) {
          this.currentIndex = currentPosition + 1;
          this.renderStep(true);
          return;
        }

        this.evaluate();
      }

      handleBack() {
        if (this.currentIndex <= 0) return;
        this.currentIndex -= 1;
        this.renderStep(true);
      }

      handleRestart() {
        this.form.reset();
        this.currentIndex = 0;
        this.lastInput = null;
        this.lastResult = null;
        this.lastExportState = null;
        this.lastCheckId = null;
        this.lastInputSummary = null;
        this.resultRegion.hidden = true;
        this.resultRegion.removeAttribute('data-status');
        this.resultRegion.replaceChildren();
        this.form.hidden = false;
        this.progress.hidden = false;
        this.updateUnknownControls();
        this.renderStep(true);
      }

      renderStep(moveFocus) {
        const state = this.getState();
        const sequence = buildStepSequence(state);
        const progressSequence = buildProgressSequence(state);
        this.currentIndex = Math.min(this.currentIndex, sequence.length - 1);
        const currentId = sequence[this.currentIndex];

        this.stepElements.forEach((element, id) => {
          element.hidden = id !== currentId;
        });

        this.updateMeasurementVisibility(state);
        const projectedPosition = progressSequence.indexOf(currentId);
        const currentNumber = projectedPosition >= 0 ? projectedPosition + 1 : this.currentIndex + 1;
        const total = Math.max(currentNumber, progressSequence.length);
        this.progressText.textContent = `Schritt ${currentNumber} von ${total}`;
        this.progressDetail.textContent = this.getStepTitle(this.stepElements.get(currentId));
        this.progressBar.setAttribute('aria-valuenow', String(currentNumber));
        this.progressBar.setAttribute('aria-valuemax', String(total));
        this.progressValue.style.width = `${(currentNumber / total) * 100}%`;
        this.backButton.hidden = this.currentIndex === 0;
        const currentStep = this.stepElements.get(currentId);
        const requiredInput = currentStep?.querySelector('input[required]');
        const currentStepComplete = !requiredInput || requiredInput.checkValidity();
        this.nextButton.textContent = currentNumber === total && currentStepComplete
          ? 'Ergebnis anzeigen'
          : 'Weiter';

        if (moveFocus) this.focusStep(this.stepElements.get(currentId));
      }

      setupStepAccessibility() {
        this.stepElements.forEach((step, stepId) => {
          const fieldset = step.querySelector('fieldset');
          const error = step.querySelector('[data-step-error]');
          if (!fieldset || !error) return;
          if (!error.id) error.id = `BimSchvStepError-${stepId}-${this.dataset.instanceId || 'checker'}`;
          fieldset.dataset.baseDescribedby = fieldset.getAttribute('aria-describedby') || '';
        });
      }

      getStepTitle(step) {
        return step?.querySelector('legend')?.textContent.trim() || '';
      }

      focusStep(step) {
        const heading = step?.querySelector('legend');
        if (!heading) return;
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
      }

      validateStep(step, stepId) {
        this.clearStepError(step);
        const requiredInput = step.querySelector('input[required]');
        if (requiredInput && !requiredInput.checkValidity()) {
          this.showStepError(step);
          requiredInput.focus({ preventScroll: true });
          return false;
        }

        if (stepId === 'measurements') return this.validateMeasurements();
        return true;
      }

      validateMeasurements() {
        const state = this.getState();
        const names = OLD_PERIODS.includes(state.period) ? ['co', 'dust'] : ['co', 'dust', 'efficiency'];
        let firstInvalid = null;

        names.forEach((name) => {
          const input = this.form.elements.namedItem(name);
          const unknown = this.form.elements.namedItem(`${name}Unknown`);
          if (unknown?.checked) {
            this.clearFieldError(name);
            return;
          }

          const parsed = parseDecimal(input?.value, name === 'efficiency' ? 100 : undefined);
          if (parsed.valid) {
            this.clearFieldError(name);
            return;
          }

          let message = 'Bitte tragen Sie einen Wert ein oder wählen Sie „Weiß ich nicht“.';
          if (!parsed.empty) {
            message = name === 'efficiency'
              ? 'Bitte geben Sie eine Zahl zwischen 0 und 100 mit Komma oder Punkt ein.'
              : 'Bitte geben Sie eine nicht negative Zahl mit Komma oder Punkt ein.';
          }
          this.showFieldError(name, message);
          if (!firstInvalid) firstInvalid = input;
        });

        if (firstInvalid) {
          this.showStepError(this.stepElements.get('measurements'));
          firstInvalid.focus({ preventScroll: true });
          return false;
        }
        return true;
      }

      showStepError(step) {
        const error = step?.querySelector('[data-step-error]');
        if (error) error.hidden = false;
        this.setStepErrorDescription(step, true);
        step
          ?.querySelectorAll('input[type="radio"], input[required]')
          .forEach((input) => input.setAttribute('aria-invalid', 'true'));
      }

      clearStepError(step) {
        const error = step?.querySelector('[data-step-error]');
        if (error) error.hidden = true;
        this.setStepErrorDescription(step, false);
        step?.querySelectorAll('[aria-invalid="true"]').forEach((input) => {
          if (!input.matches('[name="co"], [name="dust"], [name="efficiency"]')) input.removeAttribute('aria-invalid');
        });
      }

      setStepErrorDescription(step, visible) {
        const fieldset = step?.querySelector('fieldset');
        const error = step?.querySelector('[data-step-error]');
        if (!fieldset || !error) return;
        const base = fieldset.dataset.baseDescribedby || '';
        const ids = base.split(/\s+/).filter(Boolean);
        if (visible) ids.push(error.id);
        const describedBy = Array.from(new Set(ids)).join(' ');
        if (describedBy) fieldset.setAttribute('aria-describedby', describedBy);
        else fieldset.removeAttribute('aria-describedby');
      }

      showFieldError(name, message) {
        const input = this.form.elements.namedItem(name);
        const error = this.querySelector(`[data-field-error="${name}"]`);
        input?.setAttribute('aria-invalid', 'true');
        if (error) {
          error.textContent = message;
          error.hidden = false;
        }
      }

      clearFieldError(name) {
        const input = this.form.elements.namedItem(name);
        const error = this.querySelector(`[data-field-error="${name}"]`);
        input?.removeAttribute('aria-invalid');
        if (error) {
          error.textContent = '';
          error.hidden = true;
        }
      }

      updateUnknownControls() {
        this.form.querySelectorAll('[name$="Unknown"]').forEach((control) => this.updateUnknownControl(control));
      }

      updateUnknownControl(control) {
        const inputName = control.name.replace(/Unknown$/, '');
        const input = this.form.elements.namedItem(inputName);
        if (!input) return;
        if (control.checked) {
          input.value = '';
          this.clearFieldError(inputName);
        }
        input.disabled = control.checked;
      }

      updateMeasurementVisibility(state) {
        const efficiencyField = this.querySelector('[data-measurement-field="efficiency"]');
        if (efficiencyField) efficiencyField.hidden = OLD_PERIODS.includes(state.period);
      }

      collectInput() {
        const state = this.getState();
        const sequence = buildStepSequence(state);
        const includes = (stepId) => sequence.includes(stepId);
        const periodInput = this.form.querySelector('input[name="period"]:checked');
        const measurement = (name) => {
          if (!includes('measurements') || this.form.elements.namedItem(`${name}Unknown`)?.checked) return '';
          if (name === 'efficiency' && OLD_PERIODS.includes(state.period)) return '';
          const parsed = parseDecimal(this.form.elements.namedItem(name)?.value, name === 'efficiency' ? 100 : undefined);
          return parsed.valid ? parsed.value : '';
        };

        return {
          scopeConfirmed: state.scopeConfirmed === 'true',
          applianceType: includes('family') ? state.applianceType || '' : '',
          constructionDate: includes('period') ? periodInput?.dataset.constructionDate || '' : '',
          dateUnknown: includes('period') && periodInput?.dataset.dateUnknown === 'true',
          officialException: state.officialExceptionChoice === 'true',
          manufacturedBefore1950Proven:
            includes('pre1950-proof') && state.manufacturedBefore1950Choice === 'true',
          soleHeatSource: includes('sole-heat') && state.soleHeatSourceChoice === 'true',
          noncommercialStoveUnder15Kw:
            includes('cooking-exception') && state.noncommercialStoveChoice === 'true',
          masonryInsert: includes('masonry') && state.masonryInsertChoice === 'true',
          masonryDustReductionProof:
            includes('masonry-proof') ? state.masonryDustReductionProof || 'unknown' : 'unknown',
          oldProofType: includes('old-proof') ? state.oldProofType || 'none' : 'none',
          typeProofStatus: includes('type-proof') ? state.typeProofStatus || 'missing' : 'missing',
          co: measurement('co'),
          dust: measurement('dust'),
          efficiency: measurement('efficiency'),
        };
      }

      evaluate() {
        const rules = windowObject.OfenerlebnisBimSchvRules;
        if (!rules || typeof rules.evaluate !== 'function') {
          this.renderTechnicalError();
          return;
        }

        const input = this.collectInput();
        const result = rules.evaluate(input);
        const inputItems = this.buildInputSummary();
        this.lastInput = Object.freeze({ ...input });
        this.lastResult = result;
        this.lastInputSummary = Object.freeze([...inputItems]);
        let exportError = '';
        try {
          this.lastExportState = createExportSeed({
            result: this.lastResult,
            input: this.lastInput,
            inputItems: this.lastInputSummary,
          }, windowObject);
          this.lastCheckId = this.lastExportState.checkId;
        } catch (error) {
          this.lastExportState = null;
          this.lastCheckId = null;
          exportError = error instanceof Error ? error.message : 'Der Export konnte nicht vorbereitet werden.';
        }
        this.renderResult(result, inputItems, exportError);

        this.dispatchEvent(
          new windowObject.CustomEvent('bimschv:result', {
            bubbles: true,
            detail: { input: this.lastInput, result, exportState: this.lastExportState },
          })
        );
      }

      renderResult(result, inputItems, exportError) {
        const fragment = document.createDocumentFragment();
        const relevantInputItems = Array.isArray(inputItems) ? inputItems : [];
        fragment.append(
          this.createElement('h2', 'bimschv-check__print-title', 'Ergebnis Ihres Kaminofen BImSchV-Checks')
        );
        const statusBlock = this.createElement('div', 'bimschv-check__status-block');
        statusBlock.dataset.status = result.status;
        const statusContent = this.createElement('div', 'bimschv-check__status-content');
        const status = this.createElement(
          'p',
          'bimschv-check__status',
          STATUS_LABELS[result.status] || 'Ergebnis liegt vor'
        );
        const title = this.createElement('h3', 'bimschv-check__result-title', result.title);
        const summary = this.createElement(
          'p',
          'bimschv-check__summary',
          STATUS_SUMMARIES[result.status] || result.summary
        );
        statusContent.append(status, title, summary);
        statusBlock.append(statusContent);

        if (relevantInputItems.length > 0) statusBlock.append(this.createInputOverview(relevantInputItems, result));

        if (this.lastExportState) {
          const exportMeta = this.createElement('div', 'bimschv-check__export-meta');
          exportMeta.append(
            this.createElement('p', '', `Prüf-ID: ${this.lastExportState.checkId}`),
            this.createElement(
              'p',
              '',
              `Erstellt am: ${formatLocalDate(this.lastExportState.createdAt)}`
            )
          );
          statusBlock.append(exportMeta);
        }

        const displayDetails = Array.isArray(result.details)
          ? result.details.map((detail) => formatGermanMeasurementText(detail))
          : [];
        if (displayDetails.length > 0) {
          statusBlock.append(this.createListSection('Was wurde geprüft?', displayDetails, 'bimschv-check__result-group--checked'));
        }

        statusBlock.append(
          this.createDisclosure('Warum dieses Ergebnis?', [
            { type: 'paragraph', text: result.summary },
          ])
        );

        const legalItems = (result.sources || []).map((source) => source.label);
        if (legalItems.length > 0) {
          statusBlock.append(
            this.createDisclosure(
              'Rechtsgrundlage',
              legalItems.map((text) => ({ type: 'list-item', text }))
            )
          );
        }

        if (Array.isArray(result.nextSteps) && result.nextSteps.length > 0) {
          statusBlock.append(
            this.createListSection(
              'Was sollten Sie jetzt tun?',
              result.nextSteps,
              'bimschv-check__result-group--next'
            )
          );
        }

        statusBlock.append(
          this.createElement(
            'p',
            'bimschv-check__legal-note',
            'Dieses Dokument fasst das Ergebnis eines automatisierten Orientierungschecks anhand Ihrer eingegebenen Angaben und der hinterlegten bundesweiten Anforderungen der 1. BImSchV zusammen. Es ist keine behördliche Bescheinigung, Genehmigung oder rechtsverbindliche Freigabe. Individuelle Bescheide und Anforderungen der zuständigen Behörde beziehungsweise des zuständigen Schornsteinfegers bleiben maßgeblich.'
          )
        );
        fragment.append(statusBlock);

        const actions = this.createElement('div', 'bimschv-check__result-actions');
        actions.setAttribute('role', 'group');
        actions.setAttribute('aria-label', 'Ergebnis exportieren oder Check neu starten');
        if (this.lastExportState) {
          const pdfButton = this.createElement('button', 'button bimschv-check__result-action--primary', 'PDF herunterladen');
          pdfButton.type = 'button';
          pdfButton.dataset.action = 'pdf';
          const printButton = this.createElement(
            'button',
            'button button--secondary bimschv-check__result-action--secondary',
            'Drucken'
          );
          printButton.type = 'button';
          printButton.dataset.action = 'print';
          actions.append(pdfButton, printButton);
        }
        const restart = this.createElement(
          'button',
          'button button--secondary bimschv-check__result-action--tertiary',
          'Check neu starten'
        );
        restart.type = 'button';
        restart.dataset.action = 'restart';
        actions.append(restart);
        fragment.append(actions);

        const exportStatus = this.createElement('p', 'bimschv-check__export-status', exportError);
        exportStatus.dataset.exportStatus = '';
        exportStatus.setAttribute('role', exportError ? 'alert' : 'status');
        exportStatus.setAttribute('aria-live', exportError ? 'assertive' : 'polite');
        exportStatus.hidden = !exportError;
        fragment.append(exportStatus);

        const technical = document.createElement('details');
        technical.className = 'bimschv-check__technical';
        const technicalSummary = this.createElement('summary', '', 'Quellenstand und technische Angaben');
        const technicalList = document.createElement('dl');
        technicalList.append(
          this.createDefinition('Regelwerk', `${result.ruleSetId} ${result.ruleVersion}`),
          this.createDefinition('Quellenstand geprüft am', this.formatDate(result.legalReviewDate)),
          this.createDefinition('Reason Code', result.reasonCode)
        );
        technical.append(technicalSummary, technicalList);
        fragment.append(technical);

        this.resultRegion.replaceChildren(fragment);
        this.resultRegion.dataset.status = result.status;
        this.resultRegion.hidden = false;
        this.form.hidden = true;
        this.progress.hidden = true;
        this.resultRegion.focus({ preventScroll: true });
      }

      async handlePdfDownload(button) {
        if (button.disabled) return;
        if (!this.lastResult || !this.lastInput || !this.lastExportState) {
          this.setExportStatus('Es liegt kein gültiges Ergebnis für den PDF-Download vor.', true);
          return;
        }
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        this.setExportStatus('PDF-Modul wird geladen.', false);
        try {
          const pdf = await loadPdfModule(windowObject, this.pdfScriptUrl);
          this.lastExportState = pdf.createExportState(this.lastExportState);
          this.setExportStatus('PDF wird erstellt.', false);
          pdf.downloadPdf(this.lastExportState, windowObject);
          this.setExportStatus('Das PDF wurde erstellt und zum Download bereitgestellt.', false);
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unbekannter Exportfehler.';
          this.setExportStatus(`PDF konnte nicht erstellt werden: ${message}`, true);
        } finally {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }

      handlePrint(button) {
        if (!this.lastResult || !this.lastInput || !this.lastExportState) {
          this.setExportStatus('Es liegt kein gültiges Ergebnis zum Drucken vor.', true);
          return;
        }
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        const disclosures = Array.from(this.resultRegion.querySelectorAll('details'));
        const openStates = disclosures.map((element) => element.open);
        const restore = () => disclosures.forEach((element, index) => { element.open = openStates[index]; });
        try {
          if (typeof windowObject.print !== 'function') {
            throw new Error('Die Druckfunktion wird von diesem Browser nicht unterstützt.');
          }
          disclosures.forEach((element) => { element.open = true; });
          windowObject.print();
          this.setExportStatus('Der Druckdialog wurde geöffnet.', false);
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Unbekannter Druckfehler.';
          this.setExportStatus(`Drucken war nicht möglich: ${message}`, true);
        } finally {
          restore();
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }

      setExportStatus(message, isError) {
        const status = this.resultRegion.querySelector('[data-export-status]');
        if (!status) return;
        status.textContent = message;
        status.hidden = false;
        status.setAttribute('role', isError ? 'alert' : 'status');
        status.setAttribute('aria-live', isError ? 'assertive' : 'polite');
      }

      buildInputSummary() {
        const state = this.getState();
        const sequence = buildStepSequence(state);
        const items = [];
        if (state.scopeConfirmed === 'false') items.push('Prüfumfang: nicht bestätigt oder unsicher');
        if (state.officialExceptionChoice === 'true') items.push('Behördlicher Ausnahmebescheid: angegeben');
        if (sequence.includes('family') && state.applianceType) {
          items.push(`Feuerstätte: ${APPLIANCE_LABELS[state.applianceType] || state.applianceType}`);
        }
        if (sequence.includes('period') && state.period) {
          items.push(`Herstellung oder Errichtung: ${PERIOD_LABELS[state.period] || state.period}`);
        }
        if (sequence.includes('pre1950-proof') && state.manufacturedBefore1950Choice === 'true') {
          items.push('Herstellung oder Errichtung vor 1950: glaubhaft gemacht');
        }
        if (sequence.includes('sole-heat') && state.soleHeatSourceChoice === 'true') {
          items.push('Ausschließliche Wärmeversorgung der Wohneinheit: ja');
        }
        if (sequence.includes('cooking-exception') && state.noncommercialStoveChoice === 'true') {
          items.push('Nichtgewerbliche Nutzung und Nennwärmeleistung unter 15 kW: ja');
        }
        if (sequence.includes('masonry') && state.masonryInsertChoice === 'true') {
          items.push('Eingemauerter Einsatz: ja');
          if (sequence.includes('masonry-proof')) {
            items.push(
              `Nachweis zur Staubminderung: ${state.masonryDustReductionProof === 'proven' ? 'vorhanden' : 'nicht sicher nachgewiesen'}`
            );
          }
        }
        if (sequence.includes('old-proof') && state.oldProofType) {
          const labels = {
            manufacturer_test: 'Herstellerbescheinigung',
            chimney_sweep_measurement: 'Messung durch Schornsteinfegerin oder Schornsteinfeger',
            incomplete: 'Nachweis unvollständig oder unklar',
            none: 'Kein Nachweis',
          };
          items.push(`Nachweis: ${labels[state.oldProofType]}`);
        }
        if (sequence.includes('type-proof') && state.typeProofStatus) {
          const labels = { complete: 'vollständig', incomplete: 'unvollständig oder unklar', missing: 'nicht vorhanden' };
          items.push(`Typprüfungsnachweis: ${labels[state.typeProofStatus]}`);
        }
        if (sequence.includes('measurements')) {
          const values = [
            ['co', 'CO', 'g/m³'],
            ['dust', 'Staub', 'g/m³'],
            ['efficiency', 'Wirkungsgrad', '%'],
          ];
          values.forEach(([name, label, unit]) => {
            const field = this.querySelector(`[data-measurement-field="${name}"]`);
            if (field?.hidden) return;
            const unknown = this.form.elements.namedItem(`${name}Unknown`)?.checked;
            const value = this.form.elements.namedItem(name)?.value.trim();
            const displayedValue = unknown ? 'nicht bekannt' : formatGermanMeasurementText(`${value} ${unit}`);
            items.push(`${label}: ${displayedValue}`);
          });
        }
        return items;
      }

      createInputOverview(inputItems, result) {
        const wrapper = this.createElement('section', 'bimschv-check__result-overview');
        const heading = this.createElement('h4', 'bimschv-check__result-heading', 'Ihre Angaben im Überblick');
        const list = document.createElement('dl');
        list.className = 'bimschv-check__overview-grid';
        const preferredTerms = ['Feuerstätte', 'Herstellung oder Errichtung', 'Nachweis', 'Typprüfungsnachweis'];
        const selected = [];

        preferredTerms.forEach((term) => {
          const item = inputItems.find((entry) => entry.startsWith(`${term}:`));
          if (item && selected.length < 3 && !selected.includes(item)) selected.push(item);
        });
        inputItems.forEach((item) => {
          if (selected.length < 3 && !selected.includes(item)) selected.push(item);
        });

        selected.forEach((item) => {
          const separator = item.indexOf(':');
          const term = separator >= 0 ? item.slice(0, separator) : 'Angabe';
          const value = separator >= 0 ? item.slice(separator + 1).trim() : item;
          list.append(this.createOverviewItem(term, value));
        });
        list.append(
          this.createOverviewItem('Ergebnis', STATUS_LABELS[result.status] || 'Ergebnis liegt vor', 'bimschv-check__overview-item--status')
        );
        wrapper.append(heading, list);
        return wrapper;
      }

      createOverviewItem(term, value, className) {
        const item = this.createElement('div', `bimschv-check__overview-item${className ? ` ${className}` : ''}`);
        item.append(this.createElement('dt', '', term), this.createElement('dd', '', value));
        return item;
      }

      createListSection(headingText, items, modifierClass) {
        const wrapper = this.createElement(
          'section',
          `bimschv-check__result-group${modifierClass ? ` ${modifierClass}` : ''}`
        );
        const heading = this.createElement('h4', 'bimschv-check__result-heading', headingText);
        const list = document.createElement('ul');
        list.className = 'bimschv-check__result-list';
        items.forEach((item) => list.append(this.createElement('li', '', item)));
        wrapper.append(heading, list);
        return wrapper;
      }

      createDisclosure(summaryText, entries) {
        const details = document.createElement('details');
        details.className = 'bimschv-check__result-details';
        details.append(this.createElement('summary', '', summaryText));
        const paragraphs = entries.filter((entry) => entry.type === 'paragraph');
        paragraphs.forEach((entry) => details.append(this.createElement('p', '', entry.text)));
        const listItems = entries.filter((entry) => entry.type === 'list-item');
        if (listItems.length > 0) {
          const list = document.createElement('ul');
          listItems.forEach((entry) => list.append(this.createElement('li', '', entry.text)));
          details.append(list);
        }
        return details;
      }

      createDefinition(term, description) {
        const fragment = document.createDocumentFragment();
        fragment.append(this.createElement('dt', '', term), this.createElement('dd', '', description));
        return fragment;
      }

      createElement(tagName, className, text) {
        const element = document.createElement(tagName);
        if (className) element.className = className;
        element.textContent = text == null ? '' : text;
        return element;
      }

      formatDate(value) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return value || 'nicht angegeben';
        const [year, month, day] = value.split('-');
        return `${day}.${month}.${year}`;
      }

      renderTechnicalError() {
        const title = this.createElement('h3', 'bimschv-check__result-title', 'Technischer Fehler');
        const message = this.createElement(
          'p',
          'bimschv-check__summary',
          'Das Regelmodul konnte nicht geladen werden. Bitte laden Sie die Seite neu oder versuchen Sie es später erneut.'
        );
        this.resultRegion.replaceChildren(title, message);
        this.resultRegion.dataset.status = 'UNKNOWN';
        this.resultRegion.hidden = false;
        this.resultRegion.focus({ preventScroll: true });
      }

      prefersReducedMotion() {
        return windowObject.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
      }
    }

    windowObject.customElements.define('bimschv-check', BimSchvCheck);
  }

  return Object.freeze({
    STATUS_LABELS,
    parseDecimal,
    formatGermanMeasurementText,
    buildStepSequence,
    buildProgressSequence,
    initialize,
  });
});
